import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Flex, Heading, Text } from '@radix-ui/themes';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Link } from 'react-router-dom';
import { localList } from 'app/constants';
import { selectDevice } from 'store/slices/common';

import './style.scss';
import 'swiper/css';

export const MainSlider = ({ sliderList }) => {
  const deviceType = useSelector(selectDevice);
  const isMobile = deviceType === 'mobile';

  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const progressCircle = useRef(null);
  const [swiperRef, setSwiperRef] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const onAutoplayTimeLeft = (s, t, progress) => {
    progressCircle.current.style.setProperty('--progress', 1 - progress);
  };

  const play = useCallback(() => {
    swiperRef?.autoplay.start();
    setIsPlaying(true);
  }, [swiperRef]);

  const pause = useCallback(() => {
    swiperRef?.autoplay.stop();
    setIsPlaying(false);
  }, [swiperRef]);

  const handlePlaying = () => {
    isPlaying ? pause() : play();
  };

  const updateInertAttribute = useCallback(
    swiper => {
      const realIndex = swiper.realIndex;
      const total = sliderList?.slice(0, 10).length || 0;
      const prevReal = (realIndex - 1 + total) % total;
      const nextReal = (realIndex + 1) % total;

      swiper.slides.forEach(slide => {
        const slideIndex = parseInt(slide.getAttribute('data-swiper-slide-index'), 10);
        if (slideIndex === realIndex || slideIndex === prevReal || slideIndex === nextReal) {
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('inert', '');
        }
      });
    },
    [sliderList]
  );

  useEffect(() => {
    if (swiperRef) {
      pause();
      updateInertAttribute(swiperRef);
      swiperRef.on('slideChange', function () {
        updateInertAttribute(this);
      });
    }
  }, [swiperRef, pause, updateInertAttribute]);

  useEffect(() => {
    if (swiperRef && sliderList) {
      pause();
      swiperRef.loopDestroy();
      swiperRef.update();
      swiperRef.loopCreate();
      swiperRef.slideToLoop(0);
      updateInertAttribute(swiperRef);
    }
  }, [sliderList, swiperRef, pause, updateInertAttribute]);
  return (
    <Box className="main-slider">
      <Swiper
        aria-label="메인 슬라이더"
        slidesPerView={'auto'}
        spaceBetween={isMobile ? 8 : 16}
        centeredSlides={!isMobile && true}
        loop
        modules={[Autoplay, Pagination, Navigation]}
        pagination={{
          type: 'fraction',
          el: '.swiper-pagination',
          formatFractionCurrent: number => number
        }}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current
        }}
        onBeforeInit={swiper => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        autoplay={{
          delay: 2500,
          disableOnInteraction: false
        }}
        onAutoplayTimeLeft={onAutoplayTimeLeft}
        onSwiper={setSwiperRef}
      >
        {sliderList?.slice(0, 10).map((item, index) => (
          <SwiperSlide key={index} className={`banner ${localList.find(data => data.name === item.region)?.en}`}>
            <Link
              className="slide-item"
              to={item.board === '교육정보' ? `/education/detail/${item.postNo}` : `/event/detail/${item.postNo}`}
              title={`${item.region} ${item.board} - ${item.title}`}
            >
              <div className="text-wrap">
                <Text className={`chips main-local  ${localList.find(data => data.name === item.region)?.en}`}>{item.region}</Text>
                <Text className="chips">{item.board}</Text>
                <Flex direction="column" justify="between">
                  {isMobile ? (
                    <Text as="p" mt="var(--space-2)" className="ellipsis">
                      {item.title}
                    </Text>
                  ) : (
                    <Heading as="h3" mt="var(--space-5)" className="title5 ellipsis">
                      {item.title}
                    </Heading>
                  )}
                  <Text as="p" className="body5">
                    {new Date(item.startDate).getFullYear() > 2000
                      ? `일정 : ${item.startDate ? new Date(item.startDate).toISOString().split('T')[0] : 'N/A'} ~ ${
                          item.endDate ? new Date(item.endDate).toISOString().split('T')[0] : 'N/A'
                        }`
                      : ''}
                  </Text>
                </Flex>
              </div>
              <div className="image-wrap noData">{item.fileUrl && <img src={item.fileUrl} alt="" />}</div>
            </Link>
          </SwiperSlide>
        ))}
        <div className="utility">
          <Button aria-label="이전 슬라이드" className="slider-prev" ref={prevRef}>
            Prev
          </Button>
          <div className="swiper-pagination"></div>
          <div className="progress-wrap">
            <div className="progress" slot="container-end">
              <svg viewBox="0 0 33 32" ref={progressCircle} aria-hidden="true">
                <circle cx="16.5" cy="16" r="15" />
              </svg>
              <Button
                className={!isPlaying ? 'play' : 'pause'}
                onClick={handlePlaying}
                aria-pressed={isPlaying}
                aria-label={!isPlaying ? '슬라이드 재생' : '슬라이드 정지'}
              >
                {!isPlaying ? '재생' : '정지'}
              </Button>
            </div>
          </div>
          <Button aria-label="다음 슬라이드" className="slider-next" ref={nextRef}>
            Next
          </Button>
        </div>
      </Swiper>
    </Box>
  );
};

export default MainSlider;
